import { useState, useCallback } from "react";
import { ChevronDown, ChevronUp, FileText, CheckCircle2, Clock, Eye, EyeOff, Loader2, GitCompareArrows, RotateCcw, XCircle } from "lucide-react";
import type { Section, ContentItem } from "../../types/contractTemplate";
import type { ApprovedVersionItem, ApprovedSectionData } from "../../services/contractTemplateService";
import { fetchApprovedVersionDetail } from "../../services/contractTemplateService";
import { formatDisplayDateTime } from "../../utils/displayDate";

const CURRENT_KEY = "__current__";

export type CompareSelection = {
  readonly id: string;
  readonly label: string;
  readonly sublabel?: string;
};

export type SectionVersionHistoryProps = {
  readonly contractId: string;
  readonly sectionId: string;
  readonly currentSection: Section;
  /** Original extracted section (always shown at the bottom of history). Falls back to currentSection. */
  readonly extractedSection?: Section | null;
  readonly hasUnsavedEdits: boolean;
  readonly versions: ApprovedVersionItem[];
  readonly total: number;
  readonly isLoading: boolean;
  readonly onCompare?: (left: CompareSelection, right: CompareSelection) => void;
  readonly onActivate?: (versionId: string, scope?: { contractId: string; sectionId: string }) => Promise<void>;
  readonly onDeactivate?: (versionId: string, scope?: { contractId: string; sectionId: string }) => Promise<void>;
  readonly onActivateExtracted?: () => Promise<void>;
};

const formatDate = (iso: string): string => {
  try {
    return formatDisplayDateTime(iso);
  } catch {
    return iso;
  }
};

const DEFAULT_DECISION = "BusinessDecision";

const riskLevelBadgeClass = (level: string): string => {
  const v = (level || DEFAULT_DECISION).toLowerCase();
  if (v === "execdecision" || v === "green") {
    return "border-emerald-200 bg-emerald-100 text-emerald-800";
  }
  if (v === "legaldecision" || v === "red") {
    return "border-red-200 bg-red-100 text-red-800";
  }
  return "border-amber-200 bg-amber-100 text-amber-800";
};

const RiskLevelBadge = ({ level }: { readonly level: string }) => (
  <span
    className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${riskLevelBadgeClass(level)}`}
  >
    {level || DEFAULT_DECISION}
  </span>
);

export const extractContentText = (payload: Record<string, unknown>): string => {
  const content = payload.content as ContentItem[] | undefined;
  if (Array.isArray(content) && content.length > 0) {
    return content
      .map((c) => (typeof c === "string" ? c : c.text ?? ""))
      .filter(Boolean)
      .join("\n\n");
  }
  if (typeof payload.text === "string") return payload.text;
  return JSON.stringify(payload, null, 2);
};

type VersionDetailCache = Record<string, ApprovedSectionData>;

const EXTRACTED_KEY = "__extracted__";

export const SectionVersionHistory = ({
  contractId,
  sectionId,
  currentSection,
  extractedSection,
  hasUnsavedEdits,
  versions,
  total,
  isLoading,
  onCompare,
  onActivate,
  onDeactivate,
  onActivateExtracted,
}: SectionVersionHistoryProps) => {
  const extracted = extractedSection ?? currentSection;
  const [expanded, setExpanded] = useState(versions.length > 0);
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);
  const [detailCache, setDetailCache] = useState<VersionDetailCache>({});
  const [loadingDetailId, setLoadingDetailId] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [activatingVersionId, setActivatingVersionId] = useState<string | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);

  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const activeVersion = versions.find(
    (v) =>
      v.is_active &&
      (!v.contract_id || v.contract_id === contractId) &&
      (!v.source_section_id || v.source_section_id === sectionId)
  );
  const olderVersions = versions.filter((v) => v.id !== activeVersion?.id);
  const versionCount = total + 1;

  const toggleCompareMode = useCallback(() => {
    setCompareMode((p) => {
      if (p) setSelected([]);
      return !p;
    });
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  }, []);

  const handleCompare = useCallback(() => {
    if (selected.length !== 2 || !onCompare) return;

    const buildSelection = (id: string): CompareSelection => {
      if (id === CURRENT_KEY) {
        return { id: CURRENT_KEY, label: "Current (Editing)" };
      }
      if (id === EXTRACTED_KEY) {
        return { id: EXTRACTED_KEY, label: "Original Extracted" };
      }
      const v = versions.find((ver) => ver.id === id);
      if (v) {
        const context = v.contract_name ? ` · ${v.contract_name}` : "";
        return {
          id: v.id,
          label: `v${v.global_version_number}${v.is_active ? " (Active)" : ""}`,
          sublabel: `${v.approved_by} · ${formatDate(v.created_at)}${context}`,
        };
      }
      return { id, label: id };
    };

    const [a, b] = selected;
    const selA = buildSelection(a);
    const selB = buildSelection(b);

    const versionNum = (id: string) => {
      if (id === CURRENT_KEY) return Infinity;
      if (id === EXTRACTED_KEY) return -1;
      const v = versions.find((ver) => ver.id === id);
      return v?.global_version_number ?? 0;
    };

    if (versionNum(a) <= versionNum(b)) {
      onCompare(selA, selB);
    } else {
      onCompare(selB, selA);
    }
  }, [selected, versions, onCompare]);

  const handleToggleDetail = useCallback(
    async (versionId: string) => {
      if (expandedVersionId === versionId) {
        setExpandedVersionId(null);
        return;
      }
      setExpandedVersionId(versionId);
      setDetailError(null);
      if (versionId === EXTRACTED_KEY) return;
      if (detailCache[versionId]) return;
      setLoadingDetailId(versionId);
      try {
        const selectedVersion = versions.find((v) => v.id === versionId);
        const detailContractId = selectedVersion?.contract_id ?? contractId;
        const detailSectionId = selectedVersion?.source_section_id ?? sectionId;
        const detail = await fetchApprovedVersionDetail(detailContractId, detailSectionId, versionId);
        setDetailCache((prev) => ({ ...prev, [versionId]: detail }));
      } catch {
        setDetailError("Failed to load version content.");
      } finally {
        setLoadingDetailId(null);
      }
    },
    [contractId, sectionId, expandedVersionId, detailCache, versions]
  );

  const handleActivate = useCallback(
    async (versionId: string) => {
      if (!onActivate) return;
      const selectedVersion = versions.find((v) => v.id === versionId);
      const scope =
        selectedVersion?.contract_id && selectedVersion?.source_section_id
          ? { contractId: selectedVersion.contract_id, sectionId: selectedVersion.source_section_id }
          : undefined;
      setActivateError(null);
      setActivatingVersionId(versionId);
      try {
        await onActivate(versionId, scope);
      } catch (err) {
        setActivateError(err instanceof Error ? err.message : "Failed to activate version.");
      } finally {
        setActivatingVersionId(null);
      }
    },
    [onActivate, versions]
  );

  const [deactivatingVersionId, setDeactivatingVersionId] = useState<string | null>(null);

  const handleDeactivate = useCallback(
    async (versionId: string) => {
      if (!onDeactivate) return;
      const selectedVersion = versions.find((v) => v.id === versionId);
      const scope =
        selectedVersion?.contract_id && selectedVersion?.source_section_id
          ? { contractId: selectedVersion.contract_id, sectionId: selectedVersion.source_section_id }
          : undefined;
      setActivateError(null);
      setDeactivatingVersionId(versionId);
      try {
        await onDeactivate(versionId, scope);
      } catch (err) {
        setActivateError(err instanceof Error ? err.message : "Failed to deactivate version.");
      } finally {
        setDeactivatingVersionId(null);
      }
    },
    [onDeactivate, versions]
  );

  const [activatingExtracted, setActivatingExtracted] = useState(false);

  const handleActivateExtracted = useCallback(
    async () => {
      if (!onActivateExtracted) return;
      setActivateError(null);
      setActivatingExtracted(true);
      try {
        await onActivateExtracted();
      } catch (err) {
        setActivateError(err instanceof Error ? err.message : "Failed to activate extracted section.");
      } finally {
        setActivatingExtracted(false);
      }
    },
    [onActivateExtracted]
  );

  const renderContentPreview = (versionId: string) => {
    if (loadingDetailId === versionId) {
      return (
        <div className="mt-3 flex items-center gap-2 border-t border-gray-200 pt-3">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
          <span className="text-xs text-gray-400">Loading content…</span>
        </div>
      );
    }

    if (versionId === EXTRACTED_KEY) {
      if (expandedVersionId !== EXTRACTED_KEY) return null;
      const text = extracted.content.map((c) => c.text).join("\n\n");
      return (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <p className="mb-1.5 text-xs font-medium text-gray-600">Section Content</p>
          <div className="max-h-60 overflow-auto rounded-lg bg-white border border-gray-200 p-3">
            <pre className="whitespace-pre-wrap text-xs leading-relaxed text-gray-700 font-sans">
              {text || "No content available."}
            </pre>
          </div>
        </div>
      );
    }

    if (detailError && expandedVersionId === versionId && !detailCache[versionId]) {
      return (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <p className="text-xs text-red-500">{detailError}</p>
        </div>
      );
    }
    const detail = detailCache[versionId];
    if (!detail || expandedVersionId !== versionId) return null;
    const text = extractContentText(detail.section_payload);
    return (
      <div className="mt-3 border-t border-gray-200 pt-3">
        <p className="mb-1.5 text-xs font-medium text-gray-600">Section Content</p>
        <div className="max-h-60 overflow-auto rounded-lg bg-white border border-gray-200 p-3">
          <pre className="whitespace-pre-wrap text-xs leading-relaxed text-gray-700 font-sans">
            {text || "No content available."}
          </pre>
        </div>
      </div>
    );
  };

  const viewButton = (versionId: string) => {
    const isOpen = expandedVersionId === versionId;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void handleToggleDetail(versionId);
        }}
        className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
      >
        {isOpen ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        {isOpen ? "Hide" : "View"}
      </button>
    );
  };

  const checkbox = (id: string) => {
    if (!compareMode) return null;
    const checked = selected.includes(id);
    return (
      <input
        type="checkbox"
        checked={checked}
        onChange={() => toggleSelect(id)}
        className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500 shrink-0"
      />
    );
  };

  const canCompare = versions.length > 0 && onCompare;

  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="flex w-full items-center justify-between px-5 py-3.5 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">Version History</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {isLoading ? "…" : `${versionCount} version${versionCount !== 1 ? "s" : ""}`}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {activateError && (
            <div className="mx-5 mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700">
              {activateError}
            </div>
          )}
          {/* Compare toolbar */}
          {canCompare && (
            <div className="mx-5 mt-4 flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2.5 border border-gray-200">
              <div className="flex items-center gap-2">
                <GitCompareArrows className="h-4 w-4 text-gray-500" />
                {compareMode ? (
                  <span className="text-xs text-gray-600">
                    Select 2 versions to compare
                    {selected.length > 0 && (
                      <span className="ml-1 font-medium text-primary-600">({selected.length}/2 selected)</span>
                    )}
                  </span>
                ) : (
                  <span className="text-xs text-gray-500">Compare versions side by side</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {compareMode && selected.length === 2 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleCompare(); }}
                    className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-600"
                  >
                    Compare Selected
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleCompareMode(); }}
                  className={
                    compareMode
                      ? "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                      : "rounded-lg border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
                  }
                >
                  {compareMode ? "Cancel" : "Compare"}
                </button>
              </div>
            </div>
          )}

          <div
            className={`px-5 py-4 space-y-3 ${versionCount > 3 ? "max-h-[400px] overflow-y-auto" : ""}`}
          >
          {isLoading && (
            <p className="px-2 py-3 text-xs text-gray-400">Loading version history…</p>
          )}

          {/* Active approved version (top) */}
          {activeVersion && (
            <div className={`rounded-lg border-2 p-4 ${
              compareMode && selected.includes(activeVersion.id)
                ? "border-primary-300 bg-primary-50/30"
                : "border-emerald-200 bg-emerald-50/50"
            }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  {checkbox(activeVersion.id)}
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-gray-900">
                      v{activeVersion.global_version_number}
                    </p>
                    <p className="text-xs text-gray-600">
                      Approved by {activeVersion.approved_by} &middot; {formatDate(activeVersion.created_at)}
                    </p>
                    <p className="text-xs text-gray-500">
                      BUs: {activeVersion.business_units.join(", ")}
                    </p>
                    <p className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      Risk: <RiskLevelBadge level={activeVersion.content_risk_level} />
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!compareMode && viewButton(activeVersion.id)}
                  {!compareMode && onDeactivate && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDeactivate(activeVersion.id);
                      }}
                      disabled={deactivatingVersionId === activeVersion.id}
                      className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
                    >
                      {deactivatingVersionId === activeVersion.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <XCircle className="h-3 w-3" />
                      )}
                      Deactivate
                    </button>
                  )}
                  {hasUnsavedEdits && (
                    <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 border border-amber-200">
                      Editing
                    </span>
                  )}
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200">
                    ACTIVE
                  </span>
                </div>
              </div>
              {!compareMode && renderContentPreview(activeVersion.id)}
            </div>
          )}

          {/* Older versions (middle) */}
          {olderVersions.map((v) => (
            <div
              key={v.id}
              className={`rounded-lg border p-4 ${
                compareMode && selected.includes(v.id)
                  ? "border-2 border-primary-300 bg-primary-50/30"
                  : "border-gray-200 bg-gray-50/50"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  {checkbox(v.id)}
                  <FileText className="mt-0.5 h-4 w-4 text-gray-400" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-700">
                      v{v.global_version_number}
                    </p>
                    <p className="text-xs text-gray-500">
                      Approved by {v.approved_by} &middot; {formatDate(v.created_at)}
                    </p>
                    {v.contract_name && (
                      <p className="text-xs text-gray-500">
                        {v.contract_name} &middot; {v.contract_type ?? "Contract"}
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      BUs: {v.business_units.join(", ")}
                    </p>
                    <p className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                      Risk: <RiskLevelBadge level={v.content_risk_level} />
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!compareMode && viewButton(v.id)}
                  {!compareMode && v.is_active && onDeactivate && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDeactivate(v.id);
                      }}
                      disabled={deactivatingVersionId === v.id}
                      className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
                    >
                      {deactivatingVersionId === v.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <XCircle className="h-3 w-3" />
                      )}
                      Deactivate
                    </button>
                  )}
                  {!compareMode && !v.is_active && onActivate && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleActivate(v.id);
                      }}
                      disabled={activatingVersionId === v.id}
                      className="inline-flex items-center gap-1 rounded-md border border-primary-300 bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-50"
                    >
                      {activatingVersionId === v.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3 w-3" />
                      )}
                      Activate
                    </button>
                  )}
                  <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                    v{v.global_version_number}
                  </span>
                </div>
              </div>
              {!compareMode && renderContentPreview(v.id)}
            </div>
          ))}

          {/* Original extracted section (bottom) */}
          <div className={`rounded-lg border p-4 ${
            compareMode && selected.includes(EXTRACTED_KEY)
              ? "border-2 border-primary-300 bg-primary-50/30"
              : "border-gray-200 bg-slate-50/50"
          }`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                {checkbox(EXTRACTED_KEY)}
                <FileText className="mt-0.5 h-4 w-4 text-slate-400" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-700">Original Extracted</p>
                  <p className="text-xs text-gray-500">{extracted.sectionName}</p>
                  <p className="text-xs text-gray-400">
                    BUs: {extracted.businessUnit.join(", ") || "—"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!compareMode && viewButton(EXTRACTED_KEY)}
                {!compareMode && onActivateExtracted && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleActivateExtracted();
                    }}
                    disabled={activatingExtracted}
                    className="inline-flex items-center gap-1 rounded-md border border-primary-300 bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-50"
                  >
                    {activatingExtracted ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RotateCcw className="h-3 w-3" />
                    )}
                    Activate
                  </button>
                )}
                {!activeVersion && hasUnsavedEdits && (
                  <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 border border-amber-200">
                    Editing
                  </span>
                )}
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 border border-slate-200">
                  ORIGINAL
                </span>
              </div>
            </div>
            {!compareMode && renderContentPreview(EXTRACTED_KEY)}
          </div>

          {!isLoading && versions.length === 0 && (
            <p className="px-2 py-3 text-xs text-gray-400">
              No approved versions yet. Save to create the first version.
            </p>
          )}
          </div>
        </div>
      )}
    </div>
  );
};
